import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { Plus, Upload, UserPlus, Search, Filter, Download, Trash2, MessageCircle, Settings2 } from 'lucide-react';
import LeadDetailsModal from '../components/LeadDetailsModal';
import BulkImportModal from '../components/BulkImportModal';
import BulkAssignModal from '../components/BulkAssignModal';
import CreateLeadModal from '../components/CreateLeadModal';
import { getStatusColor, getStatusLabel } from '../lib/utils';
import { useLocation } from 'react-router-dom';

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [unassignedFilter, setUnassignedFilter] = useState(false);
  const [agentFilter, setAgentFilter] = useState('all');
  const [salesAgents, setSalesAgents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Archive controls (Fix #2)
  const [showArchived, setShowArchived] = useState(false);
  const [archiving, setArchiving] = useState(false);
  // Course rename/merge modal state
  const [showCourseManager, setShowCourseManager] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [pageSize] = useState(50);
  
  const user = authService.getCurrentUser();
  const location = useLocation();

  // Handle filters from navigation state
  useEffect(() => {
    if (location.state?.statusFilter) {
      setStatusFilter(location.state.statusFilter);
      setUnassignedFilter(false);
    }
    if (location.state?.unassigned) {
      setUnassignedFilter(true);
      setStatusFilter('all');
    }
    if (location.state?.openLeadId) {
      // Open lead details if navigated from reminders
      const lead = leads.find(l => l.id === location.state.openLeadId);
      if (lead) {
        setSelectedLead(lead);
        setShowDetailsModal(true);
      }
    }
  }, [location.state, leads]);

  useEffect(() => {
    fetchCourses();
    fetchSalesAgents();
  }, []);

  // Fetch leads when filters or page changes
  useEffect(() => {
    fetchLeads();
  }, [currentPage, statusFilter, courseFilter, dateFilter, searchQuery, unassignedFilter, agentFilter, showArchived]);

  const fetchCourses = async () => {
    try {
      const response = await api.getLeadCourses();
      // API returns array directly
      setCourses(Array.isArray(response.data) ? response.data : response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    }
  };

  const fetchSalesAgents = async () => {
    try {
      const response = await api.getUsers('caller');
      const callers = response.data?.filter(u => u.is_approved) || [];
      setSalesAgents(callers);
    } catch (error) {
      console.error('Error fetching sales agents:', error);
      setSalesAgents([]);
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: pageSize
      };
      
      if (statusFilter !== 'all') params.status = statusFilter;
      if (courseFilter !== 'all') params.course = courseFilter;
      if (dateFilter) params.date = dateFilter;
      if (searchQuery) params.search = searchQuery;
      if (unassignedFilter) params.unassigned = true;
      if (agentFilter !== 'all' && !unassignedFilter) params.assigned_to = agentFilter;
      if (showArchived) params.archived = true;
      
      const response = await api.getLeads(params);
      setLeads(response.data.leads);
      setTotalPages(response.data.total_pages);
      setTotalLeads(response.data.total);
      return response;
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  // Reset to page 1 when filters change
  const handleFilterChange = (setter, value) => {
    setter(value);
    setCurrentPage(1);
    setSelectedLeads([]);
  };

  const handleArchiveOld = async () => {
    // Two-step confirm: preview first, then apply
    try {
      const previewRes = await api.previewLeadsArchive({ older_than_days: 60 });
      const candidates = previewRes.data.candidates;
      if (!candidates || candidates <= 0) {
        toast.info('No old leads eligible for archival yet.');
        return;
      }
      const ok = window.confirm(
        `Archive ${candidates.toLocaleString()} leads older than 60 days (status: sold / not_interested / no_response / etc.)?\n\n` +
        `They will move to the "Archived" view and can be restored anytime.\n\n` +
        `This is safe and cannot lose data.`
      );
      if (!ok) return;
      setArchiving(true);
      const res = await api.archiveLeads({ older_than_days: 60, batch_size: 500 });
      toast.success(`Archived ${res.data.archived} leads in ${res.data.batches} batches.`);
      fetchLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archive failed');
    } finally {
      setArchiving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (courseFilter !== 'all') params.course = courseFilter;
      params.format = 'csv';

      const response = await api.exportLeads(params);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Leads exported successfully');
    } catch (error) {
      toast.error('Failed to export leads');
    } finally {
      setExporting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) {
      toast.error('No leads selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedLeads.length} lead(s)? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      const response = await api.deleteMultipleLeads(selectedLeads);
      toast.success(response.data.message);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete leads');
    } finally {
      setDeleting(false);
    }
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowDetailsModal(true);
  };

  const handleLeadSelect = (leadId) => {
    setSelectedLeads((prev) =>
      prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map((lead) => lead.id));
    }
  };

  const canManageLeads = ['admin', 'manager'].includes(user?.role);

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-headings uppercase tracking-tight text-slate-900">
            Leads Management
          </h1>
          <p className="text-slate-600 font-body mt-1">
            {totalLeads} lead{totalLeads !== 1 ? 's' : ''} found
            {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
          </p>
        </div>

        {canManageLeads && (
          <div className="flex items-center gap-3">
            <button
              data-testid="export-leads-button"
              onClick={handleExport}
              disabled={exporting || totalLeads === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-all shadow-sm font-body disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={18} />
              <span>{exporting ? 'Exporting...' : 'Export'}</span>
            </button>
            <button
              data-testid="create-lead-button"
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-all shadow-sm font-body"
            >
              <Plus size={18} />
              <span>Add Lead</span>
            </button>
            <button
              data-testid="import-leads-button"
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-slate-900 border border-slate-200 rounded-md hover:bg-slate-50 transition-all shadow-sm font-body"
            >
              <Upload size={18} />
              <span>Import</span>
            </button>
            {selectedLeads.length > 0 && (
              <>
                <button
                  data-testid="bulk-assign-button"
                  onClick={() => setShowAssignModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-all shadow-sm font-body"
                >
                  <UserPlus size={18} />
                  <span>Assign ({selectedLeads.length})</span>
                </button>
                <button
                  data-testid="bulk-delete-button"
                  onClick={handleBulkDelete}
                  disabled={deleting}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all shadow-sm font-body disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 size={18} />
                  <span>{deleting ? 'Deleting...' : `Delete (${selectedLeads.length})`}</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[300px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              data-testid="search-leads-input"
              placeholder="Search by name, phone, email, or course..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={18} className="text-slate-500" />
            <select
              data-testid="status-filter-select"
              value={statusFilter}
              onChange={(e) => handleFilterChange(setStatusFilter, e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body bg-white max-w-[180px]"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="hot">Hot</option>
              <option value="warm">Warm</option>
              <option value="not_connected">Not Connected</option>
              <option value="not_answered">Not Answered</option>
              <option value="not_interested">Not Interested</option>
              <option value="already_purchased">Already Purchased</option>
              <option value="customer">Customer</option>
              <option value="follow_up">Follow Up</option>
              <option value="follow_up_later">Follow Up Later</option>
            </select>

            {courses.length > 0 && (
              <select
                data-testid="course-filter-select"
                value={courseFilter}
                onChange={(e) => handleFilterChange(setCourseFilter, e.target.value)}
                title={courseFilter === 'all' ? 'All Courses' : courseFilter}
                className="px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body bg-white max-w-[240px] truncate"
              >
                <option value="all">All Courses</option>
                {courses.map((course) => (
                  <option key={course} value={course}>
                    {course}
                  </option>
                ))}
              </select>
            )}

            {canManageLeads && courses.length > 0 && (
              <button
                type="button"
                data-testid="manage-courses-btn"
                onClick={() => setShowCourseManager(true)}
                title="Rename or merge course names"
                className="flex items-center gap-1 px-3 py-2 border border-slate-200 rounded-md text-sm text-slate-700 hover:bg-slate-100"
              >
                <Settings2 size={16} /> Manage
              </button>
            )}

            {/* Sales Agent Filter (Admin/Manager only) */}
            {canManageLeads && salesAgents.length > 0 && (
              <select
                data-testid="agent-filter-select"
                value={agentFilter}
                onChange={(e) => {
                  handleFilterChange(setAgentFilter, e.target.value);
                  if (e.target.value !== 'all') {
                    setUnassignedFilter(false);
                  }
                }}
                disabled={unassignedFilter}
                className={`px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body bg-white max-w-[200px] ${
                  unassignedFilter ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <option value="all">All Sales Agents</option>
                {salesAgents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
            )}

            <div className="flex items-center gap-2">
              <input
                type="date"
                data-testid="date-filter-input"
                value={dateFilter}
                onChange={(e) => handleFilterChange(setDateFilter, e.target.value)}
                className="px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body bg-white"
              />
              {dateFilter && (
                <button
                  onClick={() => handleFilterChange(setDateFilter, '')}
                  className="px-2 py-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                  title="Clear date filter"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Unassigned Filter Toggle (Admin/Manager only) */}
            {canManageLeads && (
              <label className={`flex items-center gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors ${
                unassignedFilter ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}>
                <input
                  type="checkbox"
                  data-testid="unassigned-filter-checkbox"
                  checked={unassignedFilter}
                  onChange={(e) => {
                    setUnassignedFilter(e.target.checked);
                    if (e.target.checked) {
                      setStatusFilter('all');
                      setAgentFilter('all');
                    }
                    setCurrentPage(1);
                  }}
                  className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="text-sm font-medium font-body">Unassigned Only</span>
              </label>
            )}

            {/* Archive controls (Admin/Manager only) */}
            {canManageLeads && (
              <>
                <label className={`flex items-center gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors ${
                  showArchived ? 'bg-slate-800 text-white border border-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`} title="Toggle between active leads and archived (>60 days old, terminal statuses)">
                  <input
                    type="checkbox"
                    data-testid="show-archived-checkbox"
                    checked={showArchived}
                    onChange={(e) => { setShowArchived(e.target.checked); setCurrentPage(1); setSelectedLeads([]); }}
                    className="w-4 h-4 rounded border-slate-300 focus:ring-slate-500"
                  />
                  <span className="text-sm font-medium font-body">Show Archived</span>
                </label>
                {!showArchived && user?.role === 'admin' && (
                  <button
                    data-testid="archive-old-leads-btn"
                    onClick={handleArchiveOld}
                    disabled={archiving}
                    title="Move leads older than 60 days (sold/not-interested/no-response/etc.) into the archive to speed up the leads page"
                    className="px-4 py-2 rounded-md text-sm font-medium border transition-colors bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100 disabled:opacity-60"
                  >
                    {archiving ? 'Archiving…' : 'Archive Old Leads'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-slate-600 font-body">Loading leads...</div>
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {canManageLeads && (
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      data-testid="select-all-checkbox"
                      checked={selectedLeads.length === leads.length && leads.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                    />
                  </th>
                )}
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700 font-body">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700 font-body">
                  Phone
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700 font-body">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700 font-body">
                  Course
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700 font-body">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700 font-body">
                  Entry Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700 font-body">
                  Assigned To
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={canManageLeads ? 8 : 7} className="px-6 py-12 text-center text-slate-500 font-body">
                    No leads found. {canManageLeads && 'Add or import leads to get started.'}
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr
                    key={lead.id}
                    data-testid={`lead-row-${lead.id}`}
                    onClick={() => handleLeadClick(lead)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    {canManageLeads && (
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          data-testid={`select-lead-${lead.id}`}
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => handleLeadSelect(lead.id)}
                          className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                        />
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-slate-900 font-body">{lead.name}</p>
                        {lead.lead_group_name && (
                          <p className="text-xs text-slate-500 font-body">({lead.lead_group_name})</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <p className="text-slate-600 font-mono text-sm">{lead.phone}</p>
                        {lead.phone && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const phone = lead.phone.replace(/[^0-9]/g, '');
                              window.open(`https://wa.me/${phone}`, 'whatsapp_window');
                            }}
                            className="p-1.5 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"
                            title="Message on WhatsApp"
                            data-testid={`whatsapp-btn-${lead.id}`}
                          >
                            <MessageCircle size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-slate-600 font-body text-sm">{lead.email || '-'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-slate-600 font-body text-sm truncate max-w-xs" title={lead.course}>
                        {lead.course || '-'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-0.5 text-xs font-bold uppercase rounded-full border whitespace-nowrap ${getStatusColor(
                          lead.status
                        )}`}
                      >
                        {getStatusLabel(lead.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-slate-600 font-mono text-sm">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        }) : '-'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-slate-600 font-body text-sm">{lead.assigned_to_name || 'Unassigned'}</p>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Pagination Controls */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
            <div className="text-sm text-slate-600 font-body">
              Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalLeads)} of {totalLeads} leads
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-md hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed font-body"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-md hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed font-body"
              >
                Previous
              </button>
              
              <div className="flex items-center gap-1">
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1.5 text-sm rounded-md font-body ${
                        currentPage === pageNum
                          ? 'bg-slate-900 text-white'
                          : 'border border-slate-200 hover:bg-white'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-md hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed font-body"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-md hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed font-body"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showDetailsModal && (
        <LeadDetailsModal
          lead={selectedLead}
          onClose={() => {
            // Store current scroll position
            const scrollPos = window.scrollY;
            setShowDetailsModal(false);
            setSelectedLead(null);
            fetchLeads().then(() => {
              // Restore scroll position after data loads
              setTimeout(() => window.scrollTo(0, scrollPos), 0);
            });
          }}
        />
      )}

      {showImportModal && (
        <BulkImportModal
          onClose={() => {
            setShowImportModal(false);
            fetchLeads();
          }}
        />
      )}

      {showAssignModal && (
        <BulkAssignModal
          selectedLeadIds={selectedLeads}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedLeads([]);
            fetchLeads();
          }}
        />
      )}

      {showCreateModal && (
        <CreateLeadModal
          onClose={() => {
            setShowCreateModal(false);
            fetchLeads();
          }}
        />
      )}

      {showCourseManager && (
        <CourseManagerModal
          courses={courses}
          onClose={() => setShowCourseManager(false)}
          onSaved={async () => {
            await fetchCourses();
            fetchLeads();
          }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Course Manager Modal — rename a course or merge two courses into one
// ─────────────────────────────────────────────────────────────────────────────
const CourseManagerModal = ({ courses, onClose, onSaved }) => {
  const [oldName, setOldName] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const filteredCourses = courses.filter((c) =>
    c.toLowerCase().includes(search.toLowerCase())
  );

  const isMerge = !!newName && courses.includes(newName) && newName !== oldName;

  const handleSave = async () => {
    const o = oldName.trim();
    const n = newName.trim();
    if (!o) return toast.error('Pick a course to rename');
    if (!n) return toast.error('Enter a new course name');
    if (o === n) return toast.error('New name is same as old');

    const confirmText = isMerge
      ? `Merge "${o}" INTO "${n}"?\n\nAll leads currently in "${o}" will be moved to "${n}". This cannot be undone.`
      : `Rename "${o}" → "${n}" across ALL active leads?`;
    if (!window.confirm(confirmText)) return;

    setSaving(true);
    try {
      const res = await api.renameLeadCourse(o, n);
      const label = isMerge ? 'merged' : 'renamed';
      toast.success(`${res.data.modified.toLocaleString()} lead(s) ${label}`);
      await onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="course-manager-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-heading text-lg text-slate-900">Manage Courses</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="course-manager-close">✕</button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Course to rename</label>
            <input
              type="text"
              placeholder="Search courses…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2 mb-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
            <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-md">
              {filteredCourses.length === 0 && (
                <div className="p-3 text-sm text-slate-500">No courses match</div>
              )}
              {filteredCourses.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => { setOldName(c); if (!newName) setNewName(c); }}
                  data-testid={`course-row-${c}`}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-0 hover:bg-slate-50 ${
                    oldName === c ? 'bg-slate-100 font-medium' : ''
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">New name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Type a new name or an existing course to MERGE"
              data-testid="course-new-name"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
            {isMerge && (
              <div className="mt-2 flex items-start gap-2 p-2 text-xs bg-amber-50 border border-amber-200 rounded-md text-amber-800">
                <span className="font-semibold">Merge:</span>
                <span>&quot;{oldName}&quot; leads will be moved into the existing course &quot;{newName}&quot;.</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !oldName || !newName || oldName === newName}
            data-testid="course-save-btn"
            className="px-4 py-2 text-sm bg-slate-900 text-white rounded-md hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? 'Saving…' : (isMerge ? 'Merge courses' : 'Rename course')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Leads;